import { TtfTextSceneObject } from './TtfTextSceneObject';
import { Vector2 } from './Vector2';

export interface AutoHeightTarget extends TtfTextSceneObject {
  build(): void;
  getLines(): string[] | null;
}

export interface AutoHeightOptions {
  shiftSiblings?: boolean;
  fontSizeFix?: number;
}

/**
 * Utility to apply auto-height to text-like scene objects.
 * Call `apply(target)` whenever text/font parameters change to resize height
 * and optionally shift lower siblings by the height delta.
 */
export class AutoHeightDecorator {
  private readonly options: AutoHeightOptions;

  constructor(options: AutoHeightOptions = {}) {
    this.options = options;
  }

  public apply(target: AutoHeightTarget): void {
    if (!target) return;

    // Ensure lines are prepared
    target.build();
    const lines = target.getLines();
    if (!lines || lines.length === 0) {
      return;
    }

    if (target.lineSpacing === null || target.lineSpacing === undefined) {
      target.lineSpacing = 1;
    }

    const requiredHeight =
      lines.length * (target.fontSize + (this.options.fontSizeFix || 0)) +
      (lines.length - 1) * target.lineSpacing;

    if (requiredHeight <= target.size.y) {
      return;
    }

    const heightDelta = requiredHeight - target.originalTextBounds!.y;

    target.size = new Vector2(target.size.x, requiredHeight);
    target.originalTextBounds = null;

    target.invalidate();
    target.build();

    // Shift lower siblings if enabled
    if (this.options.shiftSiblings !== false && target.parent && heightDelta !== 0) {
      const myY = target.position.y;
      const siblings = target.parent.childs;

      siblings
        .filter((sibling) => sibling.position.y > myY && sibling !== target)
        .forEach((sibling) => {
          sibling.position = new Vector2(sibling.position.x, sibling.position.y + heightDelta);
        });
    }
  }
}
